import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, MenuIcon, XIcon, ArrowUpRightIcon, FlameIcon, TreesIcon, LeafIcon, ZapIcon, CoffeeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface SolutionItem {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}
const solutions: SolutionItem[] = [{
  id: 'coffee',
  icon: <CoffeeIcon className="w-5 h-5" data-id="element-634" />,
  title: 'Coffee Traceability',
  description: 'Farm-to-cup supply chain verification and EUDR compliance'
}, {
  id: 'cookstoves',
  icon: <FlameIcon className="w-5 h-5" data-id="element-635" />,
  title: 'Cookstove Carbon Credits',
  description: 'IoT-enabled DMRV for clean cooking emission reductions'
}, {
  id: 'forestry',
  icon: <TreesIcon className="w-5 h-5" data-id="element-636" />,
  title: 'Forestry & REDD+',
  description: 'Satellite-verified forest carbon monitoring and credits'
}, {
  id: 'agroforestry',
  icon: <LeafIcon className="w-5 h-5" data-id="element-637" />,
  title: 'Agroforestry & Soil Carbon',
  description: 'Soil carbon sequestration measurement and verification'
}, {
  id: 'energy',
  icon: <ZapIcon className="w-5 h-5" data-id="element-638" />,
  title: 'Renewable Energy',
  description: 'Carbon credit verification for solar and mini-grid projects'
}];
export function Navbar({
  currentPage,
  onNavigate
}: {
  currentPage: string;
  onNavigate: (page: string) => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setSolutionsOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setSolutionsOpen(false), 150);
  };
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  const isSolutionPage = solutions.some(s => s.id === currentPage);
  return <nav className="sticky top-0 z-50 w-full bg-[#FAFDF7]/90 backdrop-blur-md border-b border-carbon-geo" data-id="element-639">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-640">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-641">
          {/* Logo */}
          <button onClick={() => onNavigate('home')} className="flex-shrink-0" aria-label="CarbonSafe Home" data-id="element-642">
            <img src="https://cdn.magicpatterns.com/uploads/jGYrssm7tbfxt25FxeAxXx/CARBONSAFELOGO.png" alt="CarbonSafe" className="h-9 lg:h-10 w-auto" data-id="element-643" />
          </button>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1" data-id="element-644">
            {/* Solutions Dropdown */}
            <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-645">
              <button className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isSolutionPage || solutionsOpen ? 'text-carbon-bright' : 'text-carbon-deep hover:text-carbon-bright'}`} onClick={() => setSolutionsOpen(!solutionsOpen)} aria-expanded={solutionsOpen} aria-haspopup="true" data-id="element-646">
                Solutions
                <ChevronDownIcon className={`w-3.5 h-3.5 opacity-60 transition-transform duration-200 ${solutionsOpen ? 'rotate-180' : ''}`} data-id="element-647" />
              </button>

              <AnimatePresence data-id="element-648">
                {solutionsOpen && <motion.div initial={{
                opacity: 0,
                y: 8,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 8,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease: [0.22, 1, 0.36, 1]
              }} className="absolute top-full left-0 mt-2 w-[380px] bg-white rounded-xl shadow-xl border border-carbon-geo overflow-hidden" data-id="element-649">
                    <div className="p-2" data-id="element-650">
                      <p className="px-3 pt-2 pb-2 text-[11px] font-bold uppercase tracking-wider text-carbon-mid/60" data-id="element-651">
                        DMRV Opportunities | Uganda
                      </p>
                      {solutions.map(item => <button key={item.id} onClick={() => {
                    onNavigate(item.id);
                    setSolutionsOpen(false);
                  }} className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${currentPage === item.id ? 'bg-carbon-bright/5' : 'hover:bg-carbon-bright/5'}`} data-id="element-652">
                          <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5 ${currentPage === item.id ? 'bg-carbon-bright/15 text-carbon-bright' : 'bg-carbon-geo/50 text-carbon-mid'}`} data-id="element-653">
                            {item.icon}
                          </div>
                          <div className="min-w-0" data-id="element-654">
                            <p className={`text-sm font-semibold ${currentPage === item.id ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-655">
                              {item.title}
                            </p>
                            <p className="text-xs text-carbon-mid mt-0.5 leading-snug" data-id="element-656">
                              {item.description}
                            </p>
                          </div>
                        </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Other nav links */}
            <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-carbon-deep hover:text-carbon-bright transition-colors rounded-lg" data-id="element-657">
              Products
            </button>
            <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-carbon-deep hover:text-carbon-bright transition-colors rounded-lg" data-id="element-658">
              Resources
            </button>
            <button onClick={() => onNavigate('about')} className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${currentPage === 'about' ? 'text-carbon-bright' : 'text-carbon-deep hover:text-carbon-bright'}`} data-id="element-659">
              About
            </button>
            <button onClick={() => onNavigate('homev2')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${currentPage === 'homev2' ? 'bg-carbon-bright text-white' : 'bg-carbon-bright/10 text-carbon-bright hover:bg-carbon-bright/20'}`} data-id="element-660">
              V2
            </button>
          </div>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-3" data-id="element-661">
            <a href="#" className="text-sm font-medium text-carbon-deep hover:text-carbon-bright transition-colors" data-id="element-662">
              Log in
            </a>
            <a href="#" className="inline-flex items-center gap-2 px-5 py-2.5 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors" data-id="element-663">
              Talk to Expert
              <ArrowUpRightIcon className="w-4 h-4" data-id="element-664" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-carbon-deep" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} data-id="element-665">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-666" /> : <MenuIcon className="w-6 h-6" data-id="element-667" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-668">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="lg:hidden overflow-hidden bg-[#FAFDF7] border-b border-carbon-geo" data-id="element-669">
            <div className="px-4 py-4 space-y-1" data-id="element-670">
              {/* Solutions Accordion */}
              <button onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)} className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-carbon-deep rounded-lg" data-id="element-671">
                Solutions
                <ChevronDownIcon className={`w-4 h-4 opacity-60 transition-transform duration-200 ${mobileSolutionsOpen ? 'rotate-180' : ''}`} data-id="element-672" />
              </button>

              <AnimatePresence data-id="element-673">
                {mobileSolutionsOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-674">
                    <div className="pl-3 pr-1 pb-2 space-y-0.5" data-id="element-675">
                      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-carbon-mid/50" data-id="element-676">
                        DMRV Opportunities
                      </p>
                      {solutions.map(item => <button key={item.id} onClick={() => {
                  onNavigate(item.id);
                  setMobileOpen(false);
                  setMobileSolutionsOpen(false);
                }} className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left transition-colors ${currentPage === item.id ? 'bg-carbon-bright/5 text-carbon-bright' : 'text-carbon-deep hover:bg-carbon-bright/5'}`} data-id="element-677">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${currentPage === item.id ? 'bg-carbon-bright/15 text-carbon-bright' : 'bg-carbon-geo/50 text-carbon-mid'}`} data-id="element-678">
                            {item.icon}
                          </div>
                          <span className="text-sm font-semibold" data-id="element-679">
                            {item.title}
                          </span>
                        </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <button className="flex items-center w-full px-3 py-3 text-sm font-medium text-carbon-deep rounded-lg" data-id="element-680">
                Products
              </button>
              <button className="flex items-center w-full px-3 py-3 text-sm font-medium text-carbon-deep rounded-lg" data-id="element-681">
                Resources
              </button>
              <button onClick={() => {
            onNavigate('about');
            setMobileOpen(false);
          }} className={`flex items-center w-full px-3 py-3 text-sm font-medium rounded-lg ${currentPage === 'about' ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-682">
                About
              </button>

              <hr className="border-carbon-geo my-2" data-id="element-683" />

              <a href="#" className="block px-3 py-3 text-sm font-medium text-carbon-deep" data-id="element-684">
                Log in
              </a>
              <a href="#" className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors" data-id="element-685">
                Talk to Expert
                <ArrowUpRightIcon className="w-4 h-4" data-id="element-686" />
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}